import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import { createHeaderSlice } from "./header.slice";
import { createCanvasSlice } from "./canvas.slice";
import { createMachinesSlice } from "./machines.slice";
import { createNestedSlice } from "./nested.slice";
import { createPersistenceSlice } from "./persistence.slice";
import {
  INITIAL_DRAFT,
  type DraftLine,
  type DraftStore,
  type DraftStoreState,
  type LineDraftHeader,
} from "./types";

export type { DraftLine, DraftStore, DraftStoreState, LineDraftHeader, DraftCanvas } from "./types";

const draftStore: DraftStore = create<DraftStoreState>()((...a) => ({
  ...INITIAL_DRAFT,
  ...createHeaderSlice(...a),
  ...createCanvasSlice(...a),
  ...createMachinesSlice(...a),
  ...createNestedSlice(...a),
  ...createPersistenceSlice(...a),
}));

export interface UseLineDraftStateResult {
  draft: DraftLine;
  setHeader: (patch: Partial<LineDraftHeader>) => void;
  reset: () => void;
  store: DraftStore;
}

export function useLineDraftState(initial?: Partial<LineDraftHeader>): UseLineDraftStateResult {
  if (initial && !initializedHeaders.has(JSON.stringify(initial))) {
    draftStore.getState().setHeader(initial);
    draftStore.setState({ dirty: false });
    initializedHeaders.add(JSON.stringify(initial));
  }

  const draft = draftStore(
    useShallow((s) => ({
      header: s.header,
      canvas: s.canvas,
      machines: s.machines,
      dirty: s.dirty,
    }))
  );
  const setHeader = draftStore((s) => s.setHeader);
  const reset = draftStore((s) => s.reset);

  return { draft, setHeader, reset, store: draftStore };
}

export function useDraftStore<T>(selector: (state: DraftStoreState) => T): T {
  return draftStore(selector);
}

export function getDraftStore(): DraftStore {
  return draftStore;
}

const initializedHeaders = new Set<string>();
